(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const rawConfig = window.LM_PRICE_EXPERIMENT_CONFIG || {};
  const CLIENT_KEY = 'lm_behavior_client_id:v1';
  let memoryVisitorId = '';
  let memoryAssignment = null;
  let latestProductPricing = { baseUnitCents: 0, offerQuantity: 1 };

  function text(value, fallback = '') {
    return String(value == null ? fallback : value).trim();
  }

  function number(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function bool(value) {
    return value === true || value === 'true';
  }

  function readLocal(key) {
    try { return window.localStorage.getItem(key); } catch (_) { return null; }
  }

  function writeLocal(key, value) {
    try { window.localStorage.setItem(key, value); } catch (_) {}
  }

  function visitorId() {
    const existing = readLocal(CLIENT_KEY);
    if (existing) return existing;
    if (memoryVisitorId) return memoryVisitorId;
    memoryVisitorId = `lmprice_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    writeLocal(CLIENT_KEY, memoryVisitorId);
    return memoryVisitorId;
  }

  function hashToBucket(input) {
    let hash = 2166136261;
    for (const character of String(input || '')) {
      hash ^= character.charCodeAt(0);
      hash = Math.imul(hash, 16777619);
    }
    return Math.abs(hash >>> 0) % 100;
  }

  function rawGroups(config) {
    if (Array.isArray(config?.groups)) return config.groups;
    if (!config?.groups || typeof config.groups !== 'object') return [];
    return Object.entries(config.groups).map(([group, value]) => ({
      ...(value && typeof value === 'object' ? value : {}),
      group,
    }));
  }

  function offerOverrides(group) {
    if (!group?.offer_overrides || typeof group.offer_overrides !== 'object') return {};
    return Object.entries(group.offer_overrides).reduce((result, [rawQuantity, rawOffer]) => {
      const quantity = Math.round(number(rawQuantity, 0));
      if (quantity < 1 || !rawOffer || typeof rawOffer !== 'object') return result;
      result[quantity] = rawOffer;
      return result;
    }, {});
  }

  function normalizeGroup(group, index) {
    const quantities = Array.isArray(group?.eligible_offer_quantities)
      ? group.eligible_offer_quantities.map((value) => number(value, 0)).filter((value) => value > 0)
      : [1];
    const defaults = {
      group: text(group?.group, index === 0 ? 'A' : `G${index + 1}`),
      traffic: Math.max(0, number(group?.traffic ?? group?.traffic_ratio, 0)),
      displayPriceCents: Math.max(0, Math.round(number(group?.display_price_cents, 0))),
      fixedDiscountCents: Math.max(0, Math.round(number(group?.fixed_discount_cents, 0))),
      discountPercentage: Math.min(1, Math.max(0, number(group?.discount_percentage, 0))),
      discountCode: text(group?.discount_code),
      checkoutDiscountRedirectEnabled: bool(group?.checkout_discount_redirect_enabled),
      eligibleOfferQuantities: quantities.length ? quantities : [1],
      offerTitle: text(group?.offer_title),
      offerBody: text(group?.offer_body),
      bundleOfferBody: text(group?.bundle_offer_body),
    };
    return {
      ...defaults,
      offersByQuantity: Object.entries(offerOverrides(group)).reduce((result, [quantity, offer]) => {
        result[quantity] = {
          displayPriceCents: Math.max(0, Math.round(number(offer?.display_price_cents, defaults.displayPriceCents))),
          fixedDiscountCents: Math.max(0, Math.round(number(offer?.fixed_discount_cents, defaults.fixedDiscountCents))),
          discountPercentage: Math.min(1, Math.max(0, number(offer?.discount_percentage, defaults.discountPercentage))),
          discountCode: text(offer?.discount_code, defaults.discountCode),
          checkoutDiscountRedirectEnabled: offer?.checkout_discount_redirect_enabled == null
            ? defaults.checkoutDiscountRedirectEnabled
            : bool(offer.checkout_discount_redirect_enabled),
          offerTitle: text(offer?.offer_title, defaults.offerTitle),
          offerBody: text(offer?.offer_body, defaults.offerBody),
          bundleOfferBody: text(offer?.bundle_offer_body, defaults.bundleOfferBody),
        };
        return result;
      }, {}),
    };
  }

  function previewAccess(config) {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const previewIdMatches = text(config.preview_theme_id)
        && params.get('preview_theme_id') === text(config.preview_theme_id);
      const previewPrefix = text(config.preview_theme_prefix).replace(/\/$/, '');
      const scriptPath = text(document.currentScript?.src);
      const previewAssetMatches = previewPrefix && scriptPath.includes(`${previewPrefix}/assets/lm-price-experiments.js`);
      return bool(config.preview_enabled) && (previewIdMatches || previewAssetMatches);
    } catch (_) {
      return false;
    }
  }

  function normalizeConfig(config) {
    const groups = rawGroups(config).map(normalizeGroup);
    const totalTraffic = groups.reduce((sum, group) => sum + group.traffic, 0);
    const productHandle = text(config?.product_handle);
    const currentHandle = text(window.LM_PRICE_EXPERIMENT_PRODUCT_HANDLE);
    const matchesProduct = !!productHandle && productHandle === currentHandle;
    const valid = matchesProduct && groups.length > 0 && totalTraffic === 100;
    return {
      experimentId: text(config?.experiment_id),
      paramsVersion: text(config?.params_version, 'unversioned'),
      productHandle,
      enabled: bool(config?.enabled),
      preview: previewAccess(config),
      forceGroupEnabled: bool(config?.force_group_enabled),
      previewCheckoutEnabled: bool(config?.preview_checkout_enabled),
      controlUnitPriceCents: Math.max(0, Math.round(number(config?.control_unit_price_cents, 0))),
      groups: valid ? groups : [],
      valid,
    };
  }

  const config = normalizeConfig(rawConfig);
  const storageKey = `lm_price_assignment:${config.experimentId || 'inactive'}:v1`;
  const configSignature = JSON.stringify({
    experimentId: config.experimentId,
    paramsVersion: config.paramsVersion,
    productHandle: config.productHandle,
    enabled: config.enabled,
    preview: config.preview,
    controlUnitPriceCents: config.controlUnitPriceCents,
    groups: config.groups,
  });

  function previewGroup() {
    if (!config.preview) return null;
    try {
      const requested = text(new URLSearchParams(window.location.search || '').get('lm_price_preview_group')).toUpperCase();
      if (!requested) return config.groups[0] || null;
      return config.groups.find((group) => group.group.toUpperCase() === requested) || null;
    } catch (_) {
      return null;
    }
  }

  function forcedGroup() {
    if (!config.forceGroupEnabled) return null;
    try {
      const requested = text(new URLSearchParams(window.location.search || '').get('lm_price_force_group')).toUpperCase();
      if (!requested) return null;
      return config.groups.find((group) => group.group.toUpperCase() === requested) || null;
    } catch (_) {
      return null;
    }
  }

  function chooseGroup() {
    const bucket = hashToBucket(`${config.experimentId}|${visitorId()}`);
    let cumulative = 0;
    for (const group of config.groups) {
      cumulative += group.traffic;
      if (bucket < cumulative) return group;
    }
    return config.groups[config.groups.length - 1] || null;
  }

  function assignment() {
    const forced = forcedGroup();
    const previewed = previewGroup();
    const overridden = forced || previewed;
    if (overridden) {
      return {
        experimentId: config.experimentId,
        group: overridden.group,
        groupConfig: overridden,
        assignmentUnit: forced ? 'force_query' : 'preview_query',
        assignmentTime: new Date().toISOString(),
        preview: !forced,
      };
    }
    if (!config.valid || !config.enabled) return null;
    if (memoryAssignment?.signature === configSignature) return memoryAssignment;
    try {
      const stored = JSON.parse(readLocal(storageKey) || 'null');
      const storedGroup = config.groups.find((group) => group.group === stored?.group);
      if (stored?.signature === configSignature && storedGroup) {
        memoryAssignment = { ...stored, groupConfig: storedGroup };
        return memoryAssignment;
      }
    } catch (_) {}
    const groupConfig = chooseGroup();
    if (!groupConfig) return null;
    memoryAssignment = {
      experimentId: config.experimentId,
      group: groupConfig.group,
      groupConfig,
      assignmentUnit: 'visitor',
      assignmentTime: new Date().toISOString(),
      preview: false,
      signature: configSignature,
    };
    writeLocal(storageKey, JSON.stringify({ ...memoryAssignment, groupConfig: undefined }));
    return memoryAssignment;
  }

  function activeOffer(baseUnitCents, offerQuantity) {
    const current = assignment();
    if (!current?.groupConfig) return null;
    const base = Math.max(0, Math.round(number(baseUnitCents, 0)));
    const quantity = Math.max(1, Math.round(number(offerQuantity, 1)));
    const group = current.groupConfig;
    if (config.controlUnitPriceCents > 0 && base !== config.controlUnitPriceCents) return null;
    if (!group.eligibleOfferQuantities.includes(quantity)) return null;
    const offer = group.offersByQuantity[quantity] || group;
    if (offer.displayPriceCents <= 0) return null;
    return {
      ...current,
      baseUnitCents: base,
      displayUnitCents: offer.displayPriceCents,
      savingsCents: Math.max(0, base - offer.displayPriceCents),
      offerQuantity: quantity,
      offerConfig: offer,
    };
  }

  function displayUnitPrice(baseUnitCents, offerQuantity) {
    return activeOffer(baseUnitCents, offerQuantity)?.displayUnitCents || null;
  }

  function offerPricing(baseUnitCents, offerQuantity) {
    const active = activeOffer(baseUnitCents, offerQuantity);
    if (!active) return null;
    const paidQuantity = active.offerQuantity === 3 ? 2 : 1;
    const totalCents = active.displayUnitCents * paidQuantity;
    return {
      ...active,
      totalCents,
      compareCents: active.baseUnitCents * active.offerQuantity,
      totalSavingsCents: Math.max(0, (active.baseUnitCents * active.offerQuantity) - totalCents),
    };
  }

  function formatMoney(cents) {
    const pattern = window.LM_STORE?.moneyFormat || '${{amount}}';
    const amount = (Math.max(0, number(cents, 0)) / 100).toFixed(2);
    return pattern
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(Number(amount)).toString());
  }

  function syncNotice(baseUnitCents, offerQuantity) {
    const current = activeOffer(baseUnitCents, offerQuantity);
    document.querySelectorAll('[data-lm-price-experiment-notice]').forEach((node) => {
      const title = node.querySelector('[data-lm-price-experiment-title]');
      const body = node.querySelector('[data-lm-price-experiment-body]');
      if (!current || current.savingsCents <= 0) {
        node.hidden = true;
        node.setAttribute('data-lm-price-experiment-active', 'false');
        return;
      }
      const copy = current.offerQuantity === 3
        ? (current.offerConfig.bundleOfferBody || current.offerConfig.offerBody || 'Your price will be applied at checkout.')
        : (current.offerConfig.offerBody || 'Your price will be applied at checkout.');
      if (title) title.textContent = current.offerConfig.offerTitle || `Today’s price: ${formatMoney(current.displayUnitCents)}`;
      if (body) body.textContent = copy;
      node.hidden = false;
      node.setAttribute('data-lm-price-experiment-active', 'true');
    });
  }

  function checkoutUrl() {
    const current = assignment();
    const active = activeOffer(latestProductPricing.baseUnitCents, latestProductPricing.offerQuantity);
    const offer = active?.offerConfig;
    const checkoutEnabled = current && (config.enabled || (current.preview && config.previewCheckoutEnabled));
    if (!checkoutEnabled || !offer?.checkoutDiscountRedirectEnabled || !offer.discountCode) return '/checkout';
    return `/discount/${encodeURIComponent(offer.discountCode)}?redirect=/checkout`;
  }

  function matchingCartItem(item) {
    return text(item?.handle || item?.product_handle || item?.product?.handle) === config.productHandle;
  }

  function cartAdjustment(cart) {
    const current = assignment();
    const checkoutEnabled = current && (config.enabled || (current.preview && config.previewCheckoutEnabled));
    if (!checkoutEnabled || !current?.groupConfig) {
      return { discountCents: 0, label: '' };
    }
    const matchingItems = (cart?.items || []).map((item) => ({
      item,
      offer: activeOffer(
        config.controlUnitPriceCents,
        text(item?.properties?._offer) === 'buy2get1' ? 3 : 1,
      ),
    })).filter(({ item, offer }) => matchingCartItem(item) && Number(item?.quantity || 0) > 0 && offer?.offerConfig?.discountCode);
    if (!matchingItems.length) return { discountCents: 0, label: '' };
    const offer = matchingItems[0].offer.offerConfig;
    if (offer.discountPercentage > 0) {
      const eligibleCents = matchingItems.reduce((total, { item }) => {
        const linePrice = number(item?.final_line_price ?? item?.line_price, NaN);
        const fallbackPrice = number(item?.final_price ?? item?.price, 0) * Math.max(1, number(item?.quantity, 1));
        return total + Math.max(0, Math.round(Number.isFinite(linePrice) ? linePrice : fallbackPrice));
      }, 0);
      const discountCents = Math.max(0, Math.floor(eligibleCents * offer.discountPercentage));
      if (!discountCents) return { discountCents: 0, label: '' };
      return {
        discountCents,
        label: `${Math.round(offer.discountPercentage * 100)}% OFF at checkout`,
      };
    }
    if (offer.fixedDiscountCents <= 0) return { discountCents: 0, label: '' };
    return {
      discountCents: offer.fixedDiscountCents,
      label: `${formatMoney(offer.fixedDiscountCents)} applied at checkout`,
    };
  }

  async function prepareCheckoutAttributes() {
    const current = assignment();
    const checkoutEnabled = current && (config.enabled || (current.preview && config.previewCheckoutEnabled));
    if (!checkoutEnabled) return;
    try {
      const response = await window.fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = response?.ok ? await response.json() : null;
      const matchingItem = (cart?.items || []).find(matchingCartItem);
      if (!matchingItem) return;
      const offerQuantity = text(matchingItem?.properties?._offer) === 'buy2get1' ? 3 : 1;
      const offer = activeOffer(config.controlUnitPriceCents, offerQuantity);
      await window.fetch('/cart/update.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            lm_price_experiment_id: current.experimentId || '',
            lm_price_group: current.group || '',
            lm_price_product_handle: config.productHandle || '',
            lm_price_display_price_cents: offer ? String(offer.displayUnitCents) : '',
            lm_price_offer_quantity: String(offerQuantity),
            lm_price_display_total_cents: offer ? String(offerPricing(config.controlUnitPriceCents, offerQuantity)?.totalCents || '') : '',
            lm_price_assignment_time: current.assignmentTime || '',
            lm_price_params_version: config.paramsVersion || '',
            lm_price_preview: String(current.preview === true),
          },
        }),
      });
    } catch (_) {
      // Attribution must never block checkout.
    }
  }

  function context() {
    const current = assignment();
    if (!current) return { enabled: false, productHandle: config.productHandle };
    return {
      enabled: true,
      experimentId: current.experimentId,
      group: current.group,
      productHandle: config.productHandle,
      paramsVersion: config.paramsVersion,
      assignmentUnit: current.assignmentUnit,
      assignmentTime: current.assignmentTime,
      preview: current.preview === true,
    };
  }

  window.LM_PRICE_EXPERIMENT = {
    config,
    getAssignment: assignment,
    getDisplayUnitPrice: displayUnitPrice,
    getOfferPricing: offerPricing,
    getCartAdjustment: cartAdjustment,
    checkoutUrl,
    prepareCheckoutAttributes,
    getContext: context,
  };

  window.addEventListener('lm:product-price-synced', (event) => {
    const detail = event?.detail || {};
    latestProductPricing = {
      baseUnitCents: Math.max(0, Math.round(number(detail.baseUnitCents, 0))),
      offerQuantity: Math.max(1, Math.round(number(detail.offerQuantity, 1))),
    };
    syncNotice(detail.baseUnitCents, detail.offerQuantity);
  });
  document.documentElement.setAttribute('data-lm-price-experiment-id', config.experimentId || '');
  document.documentElement.setAttribute('data-lm-price-experiment-group', assignment()?.group || '');
  try { window.dispatchEvent(new window.CustomEvent('lm:price-experiment-ready')); } catch (_) {}
})();
